import { useMemo, useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { ModalDialog, StatusBadge, UiButton, UiLinkButton, ModelSettings, useModelSelection, loadPublicModelCatalog, MarkdownInput, readCreationDraft, saveCreationDraft } from "@ppt-agent/ui";
import {
  generationModes,
  marketingNavigation,
  supportedFiles,
  workflowSteps,
} from "./marketing-content";
import type { GenerationModeId } from "./marketing-content";
import { FlowisleMark, type MarkdownInputHandle } from "@ppt-agent/ui";
import { GenerationModeIcon } from "./generation-mode-icon";
import { coverGallery } from "./cover-gallery.generated";

function ArrowIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M4 10h11M11 5l5 5-5 5" /></svg>;
}

function SparkIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.6 6.4L21 12l-6.4 2.6L12 21l-2.6-6.4L3 12l6.4-2.6Z" /></svg>;
}

function UploadIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m7 11 5-5a2 2 0 0 1 3 3l-7 7a3.5 3.5 0 0 1-5-5l8-8a5 5 0 0 1 7 7l-7 7" /></svg>;
}

function CheckIcon() {
  return <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m4.5 10.2 3.4 3.3 7.6-7.6" /></svg>;
}

function FlowisleBrand() {
  return (
    <span className="flowisle-brand">
      <FlowisleMark />
      <strong>Flowisle</strong><b>PPT</b>
    </span>
  );
}

function SectionHeading({ id, title, note }: { id: string; title: string; note: string }) {
  return (
    <header className="section-heading">
      <h2 id={id}>{title}</h2>
      <span>{note}</span>
    </header>
  );
}

const sceneCovers = coverGallery.filter((cover) => cover.group === "scene");
const industryCovers = coverGallery.filter((cover) => cover.group === "industry");

function CoverCard({ cover }: { cover: (typeof coverGallery)[number] }) {
  return (
    <a className="cover-card" href="#create" aria-label={cover.title + " · " + cover.groupLabel + "，前往创作"}>
      <img className="cover-preview" src={cover.src} alt={cover.title + " PPT 封面"} loading="lazy" decoding="async" width="960" height="544" />
      <div className="gallery-caption"><strong>{cover.title}</strong><span>{cover.groupLabel}</span><ArrowIcon /></div>
    </a>
  );
}

export function MarketingSite() {
  const handoff = useMemo(() => readCreationDraft(typeof globalThis.location === "undefined" ? "" : globalThis.location.search), []);
  const modelSelection = useModelSelection(loadPublicModelCatalog, handoff.draft?.input.preferences);
  const [selectedMode, setSelectedMode] = useState<GenerationModeId>(handoff.draft?.input.mode ?? "professional");
  const [objective, setObjective] = useState(handoff.draft?.input.objective ?? "");
  const [selectedFiles, setSelectedFiles] = useState<string[]>(handoff.draft?.input.sourceNames ?? []);
  const [fileLimitReached, setFileLimitReached] = useState(false);
  const [formError, setFormError] = useState(handoff.error ?? "");
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [accountNotice, setAccountNotice] = useState(false);
  const menuToggle = useRef<HTMLButtonElement>(null);
  const objectiveInput = useRef<MarkdownInputHandle>(null);

  function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.currentTarget.files ?? []);
    setSelectedFiles(files.slice(0, 3).map((file) => file.name));
    setFileLimitReached(files.length > 3);
    event.currentTarget.value = "";
  }

  function startCreation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const trimmedObjective = objective.trim();
    if (!trimmedObjective) {
      setFormError("写下一点灵感，再开始。");
      objectiveInput.current?.focus();
      return;
    }
    try {
      const id = saveCreationDraft({ objective, mode: selectedMode, sourceNames: selectedFiles, preferences: modelSelection.preferences });
      // A short draft ID also restores the original composer when going back.
      globalThis.history.replaceState(globalThis.history.state, "", "/?draft=" + id + "#create");
      globalThis.location.assign("/try?draft=" + id);
    } catch (cause) {
      setFormError(cause instanceof Error ? cause.message : "暂时无法打开草稿，请重试。");
    }
  }

  return (
    <div className="marketing-site">
      <a className="marketing-skip-link" href="#main-content">跳到主要内容</a>

      <header
        className="marketing-nav"
        onKeyDown={(event) => {
          if (event.key === "Escape" && navigationOpen) {
            setNavigationOpen(false);
            menuToggle.current?.focus();
          }
        }}
      >
        <a href="#top" aria-label="Flowisle PPT 首页"><FlowisleBrand /></a>
        <button
          ref={menuToggle}
          className="marketing-menu-toggle"
          type="button"
          aria-label={navigationOpen ? "关闭导航" : "打开导航"}
          aria-expanded={navigationOpen}
          aria-controls="marketing-navigation"
          onClick={() => setNavigationOpen((value) => !value)}
        ><i /><i /></button>
        <nav id="marketing-navigation" className={navigationOpen ? "is-open" : ""} aria-label="产品导航">
          {marketingNavigation.map((item) => (
            <a
              key={item.href}
              href={item.href}
              title={item.purpose}
              aria-label={item.label + " · " + item.purpose}
              onClick={() => setNavigationOpen(false)}
            >{item.label}</a>
          ))}
        </nav>
        <div className="marketing-nav-actions">
          <button className="marketing-login" type="button" onClick={() => setAccountNotice(true)}>登录</button>
          <UiLinkButton className="marketing-button--nav" variant="primary" size="compact" href="#create" trailingIcon={<ArrowIcon />}>
            开始创作
          </UiLinkButton>
        </div>
      </header>

      <main id="main-content" tabIndex={-1}>
        <section className="marketing-hero" id="top" aria-labelledby="hero-title">
          <div className="hero-atmosphere" aria-hidden="true"><i /><i /></div>
          <div className="hero-copy">
            <h1 id="hero-title"><span>万象灵思，</span><span className="hero-title-gradient">于此奔涌</span></h1>
          </div>

          <form className="hero-composer" id="create" onSubmit={startCreation}>
            <MarkdownInput
              className="composer-input"
              inputRef={objectiveInput}
              label="演示目标"
              value={objective}
              onChange={(value) => {
                setObjective(value);
                if (formError) setFormError("");
              }}
              placeholder="写下此刻的灵感…"
              invalid={Boolean(formError)}
              describedBy={formError ? "hero-form-error" : undefined}
            />

            {selectedFiles.length ? (
              <div className="composer-files" aria-label="已选择资料">
                {selectedFiles.map((file, index) => (
                  <span key={file + "-" + index}>
                    <UploadIcon /><span>{file}</span>
                    <button
                      type="button"
                      aria-label={"移除 " + file}
                      onClick={() => {
                        setSelectedFiles((files) => files.filter((_, fileIndex) => fileIndex !== index));
                        setFileLimitReached(false);
                      }}
                    ><svg viewBox="0 0 16 16" aria-hidden="true"><path d="m4 4 8 8M12 4l-8 8" /></svg></button>
                  </span>
                ))}
              </div>
            ) : null}
            {formError ? <p className="composer-error" id="hero-form-error" role="alert">{formError}</p> : null}

            <ModelSettings selection={modelSelection} className="composer-model-settings" />
            <div className="composer-footer">
              <div className="composer-tools">
                <fieldset className="composer-modes">
                  <legend className="sr-only">生成模式</legend>
                  {generationModes.map((mode) => (
                    <label key={mode.id} className={selectedMode === mode.id ? "composer-mode is-active" : "composer-mode"} title={mode.title + " · " + mode.hint}>
                      <input
                        className="sr-only"
                        type="radio"
                        name="generationMode"
                        value={mode.id}
                        checked={selectedMode === mode.id}
                        aria-label={mode.title + " · " + mode.hint}
                        onChange={() => setSelectedMode(mode.id)}
                      />
                      <GenerationModeIcon mode={mode.id} />
                      <span className="mode-caption">{mode.title}</span>
                    </label>
                  ))}
                </fieldset>
                <label className="composer-upload" title="添加资料（最多 3 个；仅暂存文件名）">
                  <UploadIcon /><span>资料</span>
                  <input
                    className="sr-only"
                    type="file"
                    aria-label="添加资料，最多 3 个文件，仅暂存文件名"
                    multiple
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.md,.ppt,.pptx"
                    onChange={handleFiles}
                  />
                </label>
              </div>
              <UiButton className="marketing-button--primary composer-submit" variant="primary" size="large" type="submit" disabled={modelSelection.loading || Boolean(modelSelection.error)}>
                开始创作 <ArrowIcon />
              </UiButton>
            </div>
            {selectedFiles.length ? (
              <p className="composer-file-note" role="status">
                {fileLimitReached ? "仅保留前 3 个文件名；" : ""}文件名已暂存，内容未上传。
              </p>
            ) : null}
          </form>

          <div className="supported-files">
            <a href="/try">轻量试用 <ArrowIcon /></a>
            <span aria-label="支持的文件类型">{supportedFiles.join(" · ")}</span>
          </div>
        </section>

        <section className="templates-section" id="templates" aria-labelledby="templates-title">
          <SectionHeading id="templates-title" title="风格拾光" note="场景灵感 · 24" />
          <div className="cover-grid" data-cover-group="scene">
            {sceneCovers.map((cover) => <CoverCard cover={cover} key={cover.id} />)}
          </div>
        </section>

        <section className="showcase-section" id="cases" aria-labelledby="cases-title">
          <SectionHeading id="cases-title" title="佳作长廊" note="行业灵感 · 23" />
          <div className="cover-grid" data-cover-group="industry">
            {industryCovers.map((cover) => <CoverCard cover={cover} key={cover.id} />)}
          </div>
        </section>

        <section className="capability-section" id="editor" aria-labelledby="capability-title">
          <SectionHeading id="capability-title" title="精雕细琢" note="Windows 工作台 · 界面示意" />
          <div className="product-preview" aria-label="Flowisle PPT 编辑器预览">
            <div className="preview-topbar"><FlowisleBrand /><span>北区增长策略.pptx</span><StatusBadge tone="success" dot>已保存</StatusBadge></div>
            <div className="preview-workspace">
              <aside aria-label="页面缩略图示意">{[1, 2, 3, 4].map((page) => <div key={page} className={page === 2 ? "is-selected" : ""}><b>{page}</b><i /></div>)}</aside>
              <div className="preview-stage">
                <div className="preview-slide">
                  <small>GROWTH / Q3</small><h3>把远见<br />写成下一程</h3>
                  <div className="preview-slide-lines" aria-hidden="true"><i /><i /><i /></div>
                  <span className="preview-slide-page">02</span>
                </div>
              </div>
              <aside className="preview-agent">
                <header><SparkIcon /><strong>Flowisle Agent</strong></header>
                <p>这一页，已准备好。</p>
                <div><CheckIcon /> 原生文字 <b>可编辑</b></div>
                <div><CheckIcon /> 图表数据 <b>可编辑</b></div>
                <span className="preview-agent-action">继续雕琢 <ArrowIcon /></span>
              </aside>
            </div>
          </div>
          <ol className="workflow-list" aria-label="Agent 工作流示意">
            {workflowSteps.map((step) => (
              <li key={step.order}><span>{step.order}</span><h3>{step.title}</h3></li>
            ))}
          </ol>
        </section>

        <section className="enterprise-section" id="enterprise" aria-labelledby="enterprise-title">
          <div><h2 id="enterprise-title">携手成章</h2><p>团队方案筹备中</p></div>
          <UiLinkButton className="marketing-button--primary" variant="primary" size="large" href="/try">先行体验 <ArrowIcon /></UiLinkButton>
        </section>
      </main>

      <footer className="marketing-footer">
        <a href="#top" aria-label="Flowisle PPT 首页"><FlowisleBrand /></a>
        <details className="release-notes">
          <summary>使用说明</summary>
          <div>
            <p>在线版为轻量流程试用，资料仅暂存文件名，不上传内容。</p>
            <p>Windows 仅检查已授权的目录。模板、案例与工作台画面均为示意；账户与团队服务尚未开放。</p>
          </div>
        </details>
        <span className="footer-status">© 2026 Flowisle · Alpha</span>
      </footer>

      <ModalDialog
        open={accountNotice}
        title="账户暂未开放"
        description="可直接试用，无需注册。"
        onClose={() => setAccountNotice(false)}
        footer={<UiButton variant="primary" onClick={() => globalThis.location.assign("/try")}>进入轻量试用</UiButton>}
      >{null}</ModalDialog>
    </div>
  );
}
